import {type SpaceUserIndexResponse, type SpaceUserShowResponse, UserBasicInfo} from "generated-api";
import {Avatar} from "@mui/material";
import {useAppDispatch} from "store";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {OptionValue} from "model/form";
import {fetchUser, fetchUsers} from "store/user";
import {SelectFormField} from "components/form/SelectFormField";
import {useFormikContext} from "formik";
import {get} from "react-hook-form";

/** anything carrying enough of a user to build an option; a csId-keyed one has no user id */
export type UserOptionSource = Partial<UserBasicInfo & {user_in_company_space_id?: number}>;

/** the design system draws an avatar as a picture or, failing that, as initials */
export const userInitials = (name?: string) => (name || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('');

export const createUserOption = (a: UserOptionSource, useCsId?: boolean): OptionValue => {
    const pictureUrl = a.small_picture_url || a.picture_url;
    return {
        value: useCsId ? a.user_in_company_space_id! : a.id!,
        label: a.full_name || String((useCsId ? a.user_in_company_space_id : a.id)!),
        tooltip: a.email,
        // Avatar falls back to the initials both when there is no picture and when the
        // stored one fails to load -- a bare img would show a broken icon and its alt,
        // i.e. the name a second time
        icon: <Avatar src={pictureUrl} alt={''} sx={{
            width: 24, height: 24, fontSize: 10, marginRight: '8px',
            background: 'var(--color-primary-back, #eee)', color: 'inherit'
        }}>{userInitials(a.full_name)}</Avatar>
    };
};

/**
 * The name over the e-mail, avatar on the left -- the shape the AngularJS people pickers
 * use. The shared default puts both on one line, which wraps the name mid-word and leaves
 * the e-mail floating off to the right once either of them is long.
 */
export const renderUserOption = (props: React.HTMLAttributes<HTMLLIElement>, option: OptionValue) => (
    <li {...props} key={'item-' + option.value} title={option.tooltip}>
        <span className={'user-option'}>
            {option.icon}
            <span className={'user-option__text'}>
                <span className={'user-option__name'}>{option.htmlLabel || option.label}</span>
                {!!option.tooltip && <span className={'user-option__email'}>{option.tooltip}</span>}
            </span>
        </span>
    </li>
);

/** a single-select field holds the plain id, a multi one an array of them */
const asIds = (value: any, isMulti: boolean): number[] | undefined => {
    if (isMulti) {
        return value as number[] | undefined;
    }
    return value || value === 0 ? [value as number] : undefined;
};

export const UserSelectFormField = ({name, label, placeholder, filterRole, initialUsers, useCsId, shrink, isMulti = true, disabled}: {
    name: string,
    label: string,
    placeholder?: string,
    filterRole?: string,
    /** people already selected when the form opens; mandatory together with useCsId, see below */
    initialUsers?: UserOptionSource[],
    useCsId?: boolean,
    shrink?: boolean,
    /** defaults to picking several people; a single slot (one leader, one deputy) sets it false */
    isMulti?: boolean,
    disabled?: boolean,
}) => {
    const dispatch = useAppDispatch();

    const {initialValues, values} = useFormikContext();
    const valueIds = asIds(get(values as any, name), isMulti);
    const initialIds = asIds(get(initialValues as any, name), isMulti);

    const [isUserLoading, setIsUserLoading] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [userOptions, setUserOptions] = useState<OptionValue[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);

    const knownUsers = useRef<OptionValue[]>(initialUsers?.map(u => createUserOption(u, useCsId)) || []);
    const lastSearch = useRef<string>();

    const loadInitialUserOptions = useCallback(async () => {
        if (!initialIds?.length) {
            return;
        }
        if (useCsId) {
            // these ids are user_in_company_space ids, while the show endpoint below is keyed
            // by user id -- fetching by them would resolve a different person. Such a value can
            // only be named by initialUsers or by the opening search.
            return;
        }
        const missingIds = initialIds.filter((id) => !knownUsers.current.find((ku) => ku.value === id));
        if (!missingIds.length) {
            return;
        }

        // manually fetch any users initially selected and not returned by the opening search
        const users: OptionValue[] = [];
        for (const missingId of missingIds) {
            const res = await dispatch(fetchUser({id: String(missingId)}));
            const user = createUserOption(res.payload as SpaceUserShowResponse, useCsId);
            if (!knownUsers.current.find((ca) => user.value === ca.value)) {
                knownUsers.current.push(user);
            }
            users.push(user);
        }
        setUserOptions((current) => {
            const missing = users.filter(u => !current.find(c => c.value === u.value));
            if (!missing.length) {
                return current;
            }
            return [...current, ...missing];
        })

    }, [initialIds, useCsId, dispatch]);

    useEffect(() => {
        if (lastSearch.current === userSearch) {
            // no-op
            return;
        }
        setIsUserLoading(true);
        dispatch(fetchUsers({
            filterRole,
            filterFulltext: userSearch || undefined
        })).then((res) => {
            const items = res.payload as SpaceUserIndexResponse[];
            const users: OptionValue[] = items?.map(u => createUserOption(u, useCsId));
            if (valueIds?.length) {
                // always add currently selected users to the options for chips
                knownUsers.current.forEach((ca) => {
                    if (valueIds.indexOf(+ca.value) >= 0 && !users?.find((a) => a.value === ca.value)) {
                        users?.unshift(ca);
                    }
                });
            }
            // add fetched users to the local cache for future use (might be needed for the chips when next search returns something else)
            users?.forEach(a => {
                if (!knownUsers.current.find((ca) => a.value === ca.value)) {
                    knownUsers.current.push(a);
                }
            });
            setUserOptions(users);
        }).finally(() => {
            loadInitialUserOptions().finally(() => {
                setIsUserLoading(false);
                setIsInitialized(true);
                lastSearch.current = userSearch;
            });
        });
    }, [valueIds, userSearch, useCsId, loadInitialUserOptions, filterRole, dispatch]);

    return <SelectFormField name={!isInitialized ? name + 'Temp' : name}
        label={label}
        placeholder={placeholder}
        isMulti={isMulti}
        clearable={!isMulti}
        disabled={disabled}
        showTooltip
        renderOption={renderUserOption}
        options={userOptions}
        onSearch={setUserSearch}
        shrink={shrink}
        isLoading={isUserLoading}
        isInitializing={!!valueIds?.length && !isInitialized}/>;
};
